import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import type { AuthUser } from "@/services/auth.service";

const TITLES: Record<string, string> = {
  "/admin": "Dashboard",
  "/admin/properties": "Projects",
  "/admin/leads": "Leads & CRM",
  "/admin/transactions": "Transactions",
  "/admin/content": "Content",
  "/admin/media": "Media",
  "/admin/reports": "Reports",
  "/admin/users": "Users",
  "/admin/settings": "Settings",
  "/admin/security": "Security",
};

interface TopbarProps {
  user: AuthUser | null;
  onMenuClick: () => void;
  onSignOut: () => void;
}

export default function Topbar({ user, onMenuClick, onSignOut }: TopbarProps) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const title = TITLES[location.pathname] || "Admin";
  const initials = user
    ? `${(user.first_name || "A")[0]}${(user.last_name || "U")[0]}`.toUpperCase()
    : "AU";

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <header className="sticky top-0 z-30 flex items-center justify-between gap-4 h-16 px-4 md:px-6 bg-background-50/90 backdrop-blur border-b border-background-200">
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuClick}
          className="flex items-center justify-center w-9 h-9 rounded-lg text-foreground-700 hover:bg-background-100 md:hidden"
          aria-label="Open menu"
        >
          <i className="ri-menu-line text-xl"></i>
        </button>
        <div>
          <h1 className="font-heading text-lg md:text-xl text-foreground-950">{title}</h1>
          <p className="hidden sm:block text-xs text-foreground-600">
            Nomad Livings admin console
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => setSearchOpen((v) => !v)}
          className="flex items-center justify-center w-9 h-9 rounded-lg text-foreground-700 hover:bg-background-100"
          aria-label="Search"
        >
          <i className="ri-search-line text-xl"></i>
        </button>

        <button
          className="relative flex items-center justify-center w-9 h-9 rounded-lg text-foreground-700 hover:bg-background-100"
          aria-label="Notifications"
        >
          <i className="ri-notification-3-line text-xl"></i>
          <span className="absolute top-2 right-2.5 w-2 h-2 rounded-full bg-accent-500"></span>
        </button>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-background-100 transition-colors"
          >
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-600 text-background-50 text-xs font-bold">
              {initials}
            </span>
            <span className="hidden sm:flex flex-col items-start leading-tight">
              <span className="text-xs font-semibold text-foreground-950">
                {user?.first_name} {user?.last_name}
              </span>
              <span className="text-[10px] text-foreground-600">{user?.role_name || user?.role}</span>
            </span>
            <i className="ri-arrow-down-s-line text-foreground-600"></i>
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-2 w-52 rounded-2xl bg-background-50 border border-background-200 p-2 shadow-lg">
              <div className="px-3 py-2 border-b border-background-100 mb-1">
                <p className="text-sm font-semibold text-foreground-950">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-foreground-600 truncate">{user?.email}</p>
              </div>
              <a
                href="/"
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground-800 hover:bg-background-100"
              >
                <i className="ri-global-line"></i> View Website
              </a>
              <button
                onClick={onSignOut}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-accent-800 hover:bg-accent-100"
              >
                <i className="ri-logout-box-r-line"></i> Sign Out
              </button>
            </div>
          )}
        </div>
      </div>

      {searchOpen && (
        <div className="absolute top-16 left-0 right-0 md:left-[248px] bg-background-50 border-b border-background-200 px-4 md:px-6 py-3">
          <div className="flex items-center gap-2 max-w-2xl mx-auto rounded-full border border-background-300 bg-background-50 px-4 py-2">
            <i className="ri-search-line text-foreground-500"></i>
            <input
              autoFocus
              placeholder="Search projects, leads, transactions..."
              className="flex-1 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none"
            />
            <span className="text-[10px] text-foreground-500">Esc</span>
          </div>
        </div>
      )}
    </header>
  );
}